import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  User, Mail, Phone, Calendar, Clock, BookOpen, FileText,
  TrendingUp, Award, ChevronDown, ChevronUp, ArrowLeft,
  Plus, Trash2, CheckCircle, XCircle, AlertCircle, Download,
  Activity, Target, Star, BarChart2, MessageSquare, Eye, Search,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProgressBar } from "@/components/ProgressBar";
import { useAuth } from "@/auth/AuthContext";
import {
  getStudentProfile, getStudentCourseProgress, getStudentMockTestHistory,
  getStudentNotes, createStudentNote, deleteStudentNote,
  type CourseProgressItem, type MockTestHistoryItem, type MockTestAttemptDetail,
  type CategoryBreakdown, type ChapterProgressDetail,
} from "@/api/admin/studentAnalytics";
import { examConfigApi, type ExamSection } from "@/api/admin/examConfig";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(val?: string | Date | null) {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(val?: string | Date | null) {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDuration(ms: number | null) {
  if (!ms) return "—";
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function formatRelative(val?: string | null) {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.round(diffHr / 24);
  return `${diffDays}d ago`;
}

function displayName(profile: any, email: string) {
  if (!profile) return email;
  const full = `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim();
  return full || email;
}

function scoreColor(pct: number) {
  if (pct >= 80) return "text-emerald-600";
  if (pct >= 60) return "text-amber-600";
  return "text-red-500";
}

function scoreBg(pct: number) {
  if (pct >= 80) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (pct >= 60) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-red-50 text-red-600 border-red-200";
}

// ── Tab Types ─────────────────────────────────────────────────────────────────
type Tab = "profile" | "courses" | "mock-tests" | "analytics" | "timeline" | "notes";

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: "profile", label: "Profile", icon: <User size={14} /> },
  { id: "courses", label: "Course Progress", icon: <BookOpen size={14} /> },
  { id: "mock-tests", label: "Mock Tests", icon: <FileText size={14} /> },
  { id: "analytics", label: "Analytics", icon: <BarChart2 size={14} /> },
  { id: "timeline", label: "Activity Timeline", icon: <Activity size={14} /> },
  { id: "notes", label: "Admin Notes", icon: <MessageSquare size={14} /> },
];

// ── Pass/Fail Badge ───────────────────────────────────────────────────────────
function PassBadge({ passed }: { passed: boolean }) {
  return passed ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle size={10} /> Pass
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
      <XCircle size={10} /> Fail
    </span>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = "primary" }: {
  icon: React.ReactNode; label: string; value: string | number; sub?: string; color?: string;
}) {
  const colorMap: Record<string, string> = {
    primary: "bg-primary/10 text-primary",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
  };
  return (
    <KCard className="p-5">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${colorMap[color] || colorMap.primary}`}>
          {icon}
        </div>
        <div>
          <p className="text-xs text-muted-foreground font-medium">{label}</p>
          <p className="text-2xl font-bold text-foreground tracking-tight leading-none mt-0.5">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
      </div>
    </KCard>
  );
}

// ── Chapter Accordion ─────────────────────────────────────────────────────────
function ChapterAccordion({ chapter }: { chapter: ChapterProgressDetail }) {
  const [open, setOpen] = useState(false);
  const completionPct = chapter.totalLessons > 0
    ? Math.round((chapter.completedLessons / chapter.totalLessons) * 100)
    : 0;

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors text-left"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-6 h-6 rounded-lg bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0">
            {chapter.order + 1}
          </div>
          <span className="text-sm font-medium text-foreground truncate">{chapter.title}</span>
        </div>
        <div className="flex items-center gap-3 ml-4 shrink-0">
          <div className="hidden sm:flex items-center gap-1.5">
            <ProgressBar progress={completionPct} className="w-16" />
            <span className="text-xs font-semibold text-foreground w-8 text-right">{completionPct}%</span>
          </div>

          {open ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-border bg-muted/10 p-4 space-y-4">
          {/* Lesson progress */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-card rounded-lg border border-border">
              <p className="text-lg font-bold text-foreground">{chapter.completedLessons}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
            <div className="text-center p-3 bg-card rounded-lg border border-border">
              <p className="text-lg font-bold text-foreground">{chapter.remainingLessons}</p>
              <p className="text-xs text-muted-foreground">Remaining</p>
            </div>
            <div className="text-center p-3 bg-card rounded-lg border border-border">
              <p className="text-lg font-bold text-foreground">{chapter.totalLessons}</p>
              <p className="text-xs text-muted-foreground">Total Lessons</p>
            </div>
          </div>

          {/* Exam details */}
          {chapter.exam ? (
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                  <Target size={14} className="text-primary" /> Chapter Exam
                </span>
                <PassBadge passed={chapter.exam.isPassed} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <div>
                  <p className={`text-lg font-bold ${chapter.exam.bestScore !== null ? scoreColor(Math.round((chapter.exam.bestScore / chapter.exam.totalMarks) * 100)) : "text-muted-foreground"}`}>
                    {chapter.exam.bestScore !== null ? `${chapter.exam.bestScore}/${chapter.exam.totalMarks}` : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground">Best Score</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{chapter.exam.passMarks}</p>
                  <p className="text-xs text-muted-foreground">Pass Mark</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{chapter.exam.attemptsUsed}/{chapter.exam.maxAttempts}</p>
                  <p className="text-xs text-muted-foreground">Attempts</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground">{formatDate(chapter.exam.lastAttemptDate)}</p>
                  <p className="text-xs text-muted-foreground">Last Attempt</p>
                </div>
              </div>

              {/* Individual attempts */}
              {chapter.exam.attempts.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Attempt History</p>
                  <div className="space-y-2">
                    {chapter.exam.attempts.map(att => {
                      const pct = Math.round((att.score / chapter.exam!.totalMarks) * 100);
                      return (
                        <div key={att.id} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Attempt #{att.attemptNumber}</span>
                          <div className="flex items-center gap-3">
                            <span className={`font-semibold ${scoreColor(pct)}`}>{att.score}/{chapter.exam!.totalMarks} ({pct}%)</span>
                            <PassBadge passed={att.isPassed} />
                            <span className="text-muted-foreground hidden sm:inline">{formatDate(att.submittedAt)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-3 text-xs text-muted-foreground">No exam for this chapter</div>
          )}
        </div>
      )}
    </div>
  );
}

import { ExamReviewPanel } from "@/components/ExamReviewPanel";
import { getAdminMockTestAttemptReview } from "@/api/lms/mock-tests";
import type { ReviewableQuestion } from "@/components/ExamReviewPanel";

// ── Mock Test Attempt Detail Modal ────────────────────────────────────────────
function AttemptDetailModal({ attempt, mockTest, onClose }: {
  attempt: MockTestAttemptDetail;
  mockTest: MockTestHistoryItem["mockTest"];
  onClose: () => void;
}) {
  const pct = mockTest.totalMarks > 0 ? Math.round((attempt.score / mockTest.totalMarks) * 100) : 0;
  const [showReview, setShowReview] = useState(false);

  const reviewQuery = useQuery({
    queryKey: ["adminAttemptReview", mockTest.id, attempt.id],
    queryFn: () => getAdminMockTestAttemptReview(mockTest.id, attempt.id),
    enabled: showReview,
  });

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
        <div className="bg-card rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="sticky top-0 bg-card border-b border-border px-5 py-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground">Attempt #{attempt.attemptNumber}</h3>
              <p className="text-xs text-muted-foreground">{mockTest.title}</p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">✕</button>
          </div>
          <div className="p-5 space-y-4">
            {/* Score summary */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 rounded-xl bg-muted/30 border border-border">
                <p className={`text-2xl font-bold ${scoreColor(pct)}`}>{attempt.score}</p>
                <p className="text-xs text-muted-foreground">Score</p>
              </div>
              <div className="p-3 rounded-xl bg-muted/30 border border-border">
                <p className="text-2xl font-bold text-foreground">{mockTest.totalMarks}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div className="p-3 rounded-xl bg-muted/30 border border-border">
                <p className={`text-2xl font-bold ${scoreColor(pct)}`}>{pct}%</p>
                <p className="text-xs text-muted-foreground">Percentage</p>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">Pass Mark: <span className="text-foreground font-medium">{mockTest.passingMarks}</span></p>
                <p className="text-muted-foreground text-xs">Duration: <span className="text-foreground font-medium">{formatDuration(attempt.durationMs)}</span></p>
                <p className="text-muted-foreground text-xs">Date: <span className="text-foreground font-medium">{formatDateTime(attempt.submittedAt)}</span></p>
              </div>
            </div>

            {/* Answers summary */}
            {attempt.answers && Object.keys(attempt.answers).length > 0 && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Answer Summary</p>
                  <div className="text-xs text-muted-foreground bg-muted/20 rounded-lg p-3 border border-border">
                    <p>{Object.keys(attempt.answers).length} questions answered</p>
                  </div>
                </div>
                <Button variant="default" size="sm" onClick={() => setShowReview(true)}>
                  <Eye className="w-4 h-4 mr-2" />
                  Review Exam
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showReview && (
        <div className="fixed inset-0 z-[100] bg-[#f4f3f0] overflow-y-auto animate-in fade-in duration-200">
          {reviewQuery.isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="animate-spin w-8 h-8 border-4 border-stone-800 border-t-transparent rounded-full" />
            </div>
          ) : reviewQuery.data ? (
            <ExamReviewPanel
              questions={reviewQuery.data as ReviewableQuestion[]}
              answers={(attempt.answers as Record<string, unknown>) || {}}
              attemptId={attempt.id}
              title={`Review: ${mockTest.title} (Attempt #${attempt.attemptNumber})`}
              onClose={() => setShowReview(false)}
              examType={mockTest.examType as any}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center p-4">
              <p className="text-red-600 mb-4">Could not load review data. {reviewQuery.error ? String(reviewQuery.error) : ""}</p>
              <button onClick={() => setShowReview(false)} className="px-4 py-2 bg-stone-200 rounded">Close</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ── Course Progress Tab ───────────────────────────────────────────────────────
function CourseProgressTab({ userId }: { userId: number }) {
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("NEWEST");
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const { data: courses, isLoading } = useQuery({
    queryKey: ["studentCourseProgress", userId],
    queryFn: () => getStudentCourseProgress(userId),
  });

  const availableLevels = useMemo(() => {
    if (!courses) return [];
    const set = new Set<string>();
    courses.forEach(c => {
      if (c.course.levels) c.course.levels.forEach(l => set.add(l));
      else if (c.course.level) set.add(c.course.level);
    });
    return Array.from(set).sort();
  }, [courses]);

  const filteredCourses = useMemo(() => {
    if (!courses) return [];
    return courses
      .filter(c => c.course.title.toLowerCase().includes(search.toLowerCase()))
      .filter(c => {
        if (levelFilter === "ALL") return true;
        const levels = c.course.levels || [];
        return levels.includes(levelFilter) || c.course.level === levelFilter;
      })
      .sort((a, b) => {
        const da = new Date(a.enrolledAt).getTime();
        const db = new Date(b.enrolledAt).getTime();
        return sortOrder === "NEWEST" ? db - da : da - db;
      });
  }, [courses, search, levelFilter, sortOrder]);

  useMemo(() => setPage(1), [search, levelFilter, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredCourses.length / pageSize));
  const paginatedCourses = filteredCourses.slice((page - 1) * pageSize, page * pageSize);

  if (isLoading) return <LoadingState label="Loading course progress…" />;
  if (!courses?.length) return <EmptyState label="No courses enrolled yet." />;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            placeholder="Search courses..." 
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select 
          className="h-10 px-3 py-2 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={levelFilter}
          onChange={e => setLevelFilter(e.target.value)}
        >
          <option value="ALL">All Levels</option>
          {availableLevels.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select 
          className="h-10 px-3 py-2 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
        >
          <option value="NEWEST">Newest Enrolled</option>
          <option value="OLDEST">Oldest Enrolled</option>
        </select>
      </div>

      {paginatedCourses.length === 0 ? (
        <EmptyState label="No courses match your filters." />
      ) : (
        <div className="space-y-4">
          {paginatedCourses.map(course => {
        const isOpen = expandedCourse === course.enrollmentId;
        return (
          <KCard key={course.enrollmentId} className="overflow-hidden">
            <button
              onClick={() => setExpandedCourse(isOpen ? null : course.enrollmentId)}
              className="w-full p-5 flex items-center gap-4 hover:bg-muted/20 transition-colors text-left"
            >
              {/* Thumbnail */}
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                {course.course.thumbnail
                  ? <img src={course.course.thumbnail} alt="" className="w-full h-full object-cover" />
                  : <BookOpen size={20} className="text-primary" />
                }
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-foreground truncate">{course.course.title}</p>
                  <div className="flex items-center gap-2 shrink-0">
                    {course.progress.isCompleted && (
                      <span className="dash-badge bg-emerald-50 text-emerald-700">Completed</span>
                    )}
                    <span className="dash-badge bg-secondary text-secondary-foreground">
                      {course.course.levels?.join(", ") || course.course.level || "—"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <ProgressBar progress={course.progress.percentage} className="flex-1 max-w-40" />
                  <span className="text-sm font-bold text-foreground">{Math.round(course.progress.percentage)}%</span>
                  <span className="text-xs text-muted-foreground hidden sm:inline">
                    Enrolled {formatDate(course.enrolledAt)}
                  </span>
                </div>
              </div>
              {isOpen ? <ChevronUp size={16} className="text-muted-foreground shrink-0" /> : <ChevronDown size={16} className="text-muted-foreground shrink-0" />}
            </button>

            {isOpen && (
              <div className="border-t border-border p-5 space-y-3">
                {/* Quick stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="text-center p-3 bg-muted/20 rounded-xl">
                    <p className="text-lg font-bold text-foreground">{course.progress.completedLessons}</p>
                    <p className="text-xs text-muted-foreground">Lessons Done</p>
                  </div>
                  <div className="text-center p-3 bg-muted/20 rounded-xl">
                    <p className="text-lg font-bold text-foreground">{course.chapters.length}</p>
                    <p className="text-xs text-muted-foreground">Chapters</p>
                  </div>
                  <div className="text-center p-3 bg-muted/20 rounded-xl">
                    <p className="text-xs font-medium text-foreground">{formatDate(course.progress.lastAccessedAt)}</p>
                    <p className="text-xs text-muted-foreground">Last Access</p>
                  </div>
                  <div className="text-center p-3 bg-muted/20 rounded-xl">
                    <p className="text-sm font-semibold text-foreground">{course.course.type}</p>
                    <p className="text-xs text-muted-foreground">Type</p>
                  </div>
                </div>

                {/* Chapter accordion */}
                <div className="space-y-2">
                  {course.chapters.map(ch => (
                    <ChapterAccordion key={ch.id} chapter={ch} />
                  ))}
                  {!course.chapters.length && (
                    <p className="text-xs text-muted-foreground text-center py-4">No chapters found</p>
                  )}
                </div>
</div>
            )}
          </KCard>
        );
      })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 border-t border-border pt-4">
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mock Tests Tab ────────────────────────────────────────────────────────────
function MockTestsTab({ userId }: { userId: number }) {
  const [expandedTest, setExpandedTest] = useState<string | null>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<{ attempt: MockTestAttemptDetail; mockTest: MockTestHistoryItem["mockTest"] } | null>(null);
  
  const [search, setSearch] = useState("");
  const [examTypeFilter, setExamTypeFilter] = useState("ALL");
  const [levelFilter, setLevelFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("NEWEST");
  const [page, setPage] = useState(1);
  const pageSize = 5;

  // Load exam configs to resolve section codes → display names
  const { data: examConfigs = [] } = useQuery({
    queryKey: ["activeExamConfigs"],
    queryFn: () => examConfigApi.getActive(),
    staleTime: 1000 * 60 * 10,
  });

  // Build a flat map: sectionCode → ExamSection (for any exam type)
  const sectionMap = useMemo(() => {
    const map = new Map<string, ExamSection>();
    examConfigs.forEach(cfg => {
      (cfg.sections || []).forEach(sec => map.set(sec.code, sec));
    });
    return map;
  }, [examConfigs]);

  // Resolve a raw section key to a human-readable label
  const resolveSectionName = (key: string): string => {
    const sec = sectionMap.get(key);
    if (sec) {
      return sec.japaneseName
        ? `${sec.japaneseName} (${sec.englishName || sec.name})`
        : (sec.englishName || sec.name);
    }
    // Fallback: prettify the raw key (e.g. CONVERSATION_EXPRESSION → Conversation Expression)
    return key
      .split("_")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  const { data: history, isLoading } = useQuery({
    queryKey: ["studentMockTestHistory", userId],
    queryFn: () => getStudentMockTestHistory(userId),
  });

  const availableLevels = useMemo(() => {
    if (!history) return [];
    const set = new Set<string>();
    history.forEach(item => {
      if (examTypeFilter !== "ALL" && item.mockTest.examType !== examTypeFilter) return;
      if (item.mockTest.level) set.add(item.mockTest.level);
    });
    return Array.from(set).sort();
  }, [history, examTypeFilter]);

  useEffect(() => {
    setLevelFilter("ALL");
  }, [examTypeFilter]);

  const filteredHistory = useMemo(() => {
    if (!history) return [];
    return history
      .filter(item => item.mockTest.title.toLowerCase().includes(search.toLowerCase()))
      .filter(item => examTypeFilter === "ALL" || item.mockTest.examType === examTypeFilter)
      .filter(item => levelFilter === "ALL" || item.mockTest.level === levelFilter)
      .sort((a, b) => {
        // Fallback to mockTest creation or attempt date if purchase is null
        const da = a.purchase?.purchasedAt ? new Date(a.purchase.purchasedAt).getTime() : 
                  (a.attempts[0]?.startedAt ? new Date(a.attempts[0].startedAt).getTime() : 0);
        const db = b.purchase?.purchasedAt ? new Date(b.purchase.purchasedAt).getTime() : 
                  (b.attempts[0]?.startedAt ? new Date(b.attempts[0].startedAt).getTime() : 0);
        return sortOrder === "NEWEST" ? db - da : da - db;
      });
  }, [history, search, examTypeFilter, levelFilter, sortOrder]);

  useMemo(() => setPage(1), [search, examTypeFilter, levelFilter, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / pageSize));
  const paginatedHistory = filteredHistory.slice((page - 1) * pageSize, page * pageSize);

  if (isLoading) return <LoadingState label="Loading mock test history…" />;
  if (!history?.length) return <EmptyState label="No mock tests available yet." />;

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            placeholder="Search mock tests..." 
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select 
          className="h-10 px-3 py-2 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={examTypeFilter}
          onChange={e => setExamTypeFilter(e.target.value)}
        >
          <option value="ALL">All Types</option>
          <option value="JLPT">JLPT</option>
          <option value="JFT_BASIC">JFT Basic</option>
        </select>
        <select 
          className="h-10 px-3 py-2 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={levelFilter}
          onChange={e => setLevelFilter(e.target.value)}
        >
          <option value="ALL">All Levels</option>
          {availableLevels.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select 
          className="h-10 px-3 py-2 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
        >
          <option value="NEWEST">Newest Date</option>
          <option value="OLDEST">Oldest Date</option>
        </select>
      </div>

      {paginatedHistory.length === 0 ? (
        <EmptyState label="No mock tests match your filters." />
      ) : (
        <div className="space-y-4">
          {paginatedHistory.map(item => {
          const keyId = item.purchase?.id || item.mockTest.id;
          const isOpen = expandedTest === keyId;
          const bestPct = item.mockTest.totalMarks > 0 && item.stats.bestScore !== null
            ? Math.round((item.stats.bestScore / item.mockTest.totalMarks) * 100)
            : null;

          return (
            <KCard key={keyId} className="overflow-hidden">
              <button
                onClick={() => setExpandedTest(isOpen ? null : keyId)}
                className="w-full p-5 flex items-center gap-4 hover:bg-muted/20 transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <FileText size={20} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground">{item.mockTest.title}</p>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="dash-badge bg-secondary text-secondary-foreground">{item.mockTest.examType}</span>
                      <span className="dash-badge bg-secondary text-secondary-foreground">{item.mockTest.level}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                    {item.purchase ? (
                      <>
                        {item.purchase?.purchasedAt && <span>Purchased {formatDate(item.purchase.purchasedAt)}</span>}
                        <span>{item.stats.attemptsUsed}/{item.purchase?.purchasedAttempts || "Unlimited"} attempts used</span>
                      </>
                    ) : (
                      <>
                        <span>Free Test</span>
                        <span>•</span>
                        <span>{item.stats.attemptsUsed} attempts</span>
                      </>
                    )}
                    {bestPct !== null && (
                      <>
                        <span>•</span>
                        <span className={`font-semibold ${scoreColor(bestPct)}`}>Best: {bestPct}%</span>
                      </>
                    )}
                  </div>
                </div>

                {isOpen ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
              </button>

              {isOpen && (
                  <div className="p-5 space-y-5">
                    {/* Stats row */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {[
                        { label: "Total Attempts", value: item.stats.attemptsUsed },
                        { label: "Passed", value: item.stats.passedAttempts, color: "text-emerald-600" },
                        { label: "Failed", value: item.stats.failedAttempts, color: "text-red-600" },
                        { label: "Avg Score", value: item.stats.averageScore !== null ? `${Math.round(item.stats.averageScore)}%` : "—" },
                        { label: "Best Score", value: item.stats.bestScore !== null ? `${Math.round((item.stats.bestScore / item.mockTest.totalMarks) * 100)}%` : "—", color: item.stats.bestScore !== null ? scoreColor(Math.round((item.stats.bestScore / item.mockTest.totalMarks) * 100)) : "" },
                        { label: "Latest Score", value: item.stats.latestScore !== null ? `${Math.round((item.stats.latestScore / item.mockTest.totalMarks) * 100)}%` : "—", color: item.stats.latestScore !== null ? scoreColor(Math.round((item.stats.latestScore / item.mockTest.totalMarks) * 100)) : "" },
                      ].map((stat, i) => (
                        <div key={i} className="text-center p-3 bg-muted/20 rounded-xl border border-border">
                          <p className={`text-lg font-bold ${stat.color || "text-foreground"}`}>{stat.value}</p>
                          <p className="text-xs text-muted-foreground">{stat.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Attempts List */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Attempt History</p>
                      {item.attempts.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-3 bg-muted/10 rounded-xl border border-dashed border-border">No attempts yet</p>
                      ) : (
                        <div className="space-y-3">
                          {item.attempts.map(att => {
                            const pct = item.mockTest.totalMarks > 0
                              ? Math.round((att.score / item.mockTest.totalMarks) * 100)
                              : 0;
                            return (
                              <div key={att.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-card rounded-xl border border-border shadow-sm gap-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <span className="text-sm font-bold text-primary">#{att.attemptNumber}</span>
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`text-base font-bold ${scoreColor(pct)}`}>
                                        {att.score}/{item.mockTest.totalMarks} ({pct}%)
                                      </span>
                                      <PassBadge passed={att.isPassed} />
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                      <span className="flex items-center gap-1"><Calendar size={12} /> {formatDate(att.submittedAt)}</span>
                                      <span className="flex items-center gap-1"><Clock size={12} /> {formatDuration(att.durationMs)}</span>
                                    </div>
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="shrink-0 w-full sm:w-auto gap-1.5"
                                  onClick={() => setSelectedAttempt({ attempt: att, mockTest: item.mockTest })}
                                >
                                  <Eye size={14} /> Review Answers
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Category breakdown from latest attempt */}
                    {item.latestCategoryBreakdown && item.latestCategoryBreakdown.length > 0 && (
                      <div className="mt-2 border-t border-border pt-4">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Latest Attempt — Category Breakdown</p>
                        <div className="space-y-3">
                          {item.latestCategoryBreakdown.map(cat => (
                            <div key={cat.name} className="flex items-center gap-2 min-w-0">
                              {/* Section label — resolved from exam config, with ellipsis overflow */}
                              <span
                                className="text-xs text-foreground shrink-0 truncate"
                                style={{ width: "11rem" }}
                                title={resolveSectionName(cat.name)}
                              >
                                {resolveSectionName(cat.name)}
                              </span>
                              {/* Progress bar — flex-1 so it fills remaining space, never overflows */}
                              <div className="flex-1 min-w-0">
                                <ProgressBar progress={cat.percentage} />
                              </div>
                              <span className={`text-xs font-bold w-9 text-right shrink-0 ${scoreColor(cat.percentage)}`}>
                                {cat.percentage}%
                              </span>
                              {cat.isPassed !== null && (
                                <div className="shrink-0">
                                  <PassBadge passed={cat.isPassed} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </KCard>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 border-t border-border pt-4">
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
          </div>
        </div>
      )}

      {selectedAttempt && (
        <AttemptDetailModal
          attempt={selectedAttempt.attempt}
          mockTest={selectedAttempt.mockTest}
          onClose={() => setSelectedAttempt(null)}
        />
      )}
    </>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────────────────────
function AnalyticsTab({ userId }: { userId: number }) {
  const { data: mockHistory } = useQuery({
    queryKey: ["studentMockTestHistory", userId],
    queryFn: () => getStudentMockTestHistory(userId),
  });
  const { data: courses } = useQuery({
    queryKey: ["studentCourseProgress", userId],
    queryFn: () => getStudentCourseProgress(userId),
  });

  // Score trend: latest attempt per mock test over time
  const scoreTrend = useMemo(() => {
    if (!mockHistory) return [];
    return mockHistory
      .flatMap(item =>
        item.attempts.map(att => ({
          date: att.submittedAt ? new Date(att.submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "",
          score: item.mockTest.totalMarks > 0 ? Math.round((att.score / item.mockTest.totalMarks) * 100) : 0,
          test: item.mockTest.title,
        }))
      )
      .filter(d => d.date)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-20);
  }, [mockHistory]);

  // Aggregate category performance across all latest attempts
  const categoryData = useMemo(() => {
    if (!mockHistory) return [];
    const categoryMap: Record<string, { earned: number; total: number }> = {};
    for (const item of mockHistory) {
      if (item.latestCategoryBreakdown) {
        for (const cat of item.latestCategoryBreakdown) {
          if (!categoryMap[cat.name]) categoryMap[cat.name] = { earned: 0, total: 0 };
          categoryMap[cat.name].earned += cat.earned;
          categoryMap[cat.name].total += cat.total;
        }
      }
    }
    return Object.entries(categoryMap).map(([name, d]) => ({
      name,
      percentage: d.total > 0 ? Math.round((d.earned / d.total) * 100) : 0,
    })).sort((a, b) => b.percentage - a.percentage);
  }, [mockHistory]);

  // Pass/Fail donut
  const passFailData = useMemo(() => {
    if (!mockHistory) return [];
    let passed = 0, failed = 0;
    for (const item of mockHistory) {
      for (const att of item.attempts) {
        if (att.isPassed) passed++; else failed++;
      }
    }
    return [
      { name: "Passed", value: passed, color: "#10b981" },
      { name: "Failed", value: failed, color: "#ef4444" },
    ].filter(d => d.value > 0);
  }, [mockHistory]);

  // Course progress chart
  const courseChart = useMemo(() => {
    if (!courses) return [];
    return courses.slice(0, 8).map(c => ({
      name: c.course.title.slice(0, 20) + (c.course.title.length > 20 ? "…" : ""),
      progress: Math.round(c.progress.percentage),
    }));
  }, [courses]);

  const COLORS = ["#de4747", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#8b5cf6"];

  // Weak / Strong areas
  const weakAreas = categoryData.filter(c => c.percentage < 70);
  const strongAreas = categoryData.filter(c => c.percentage >= 80);

  return (
    <div className="space-y-6">
      {/* Summary flags */}
      {(weakAreas.length > 0 || strongAreas.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4">
          {strongAreas.length > 0 && (
            <KCard className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Star size={14} className="text-emerald-600" />
                <p className="text-sm font-semibold text-foreground">Strong Areas</p>
              </div>
              <div className="space-y-2">
                {strongAreas.map(a => (
                  <div key={a.name} className="flex items-center justify-between">
                    <span className="text-xs text-foreground">{a.name}</span>
                    <span className="text-xs font-bold text-emerald-600">{a.percentage}%</span>
                  </div>
                ))}
              </div>
            </KCard>
          )}
          {weakAreas.length > 0 && (
            <KCard className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle size={14} className="text-red-500" />
                <p className="text-sm font-semibold text-foreground">Needs Improvement</p>
              </div>
              <div className="space-y-2">
                {weakAreas.map(a => (
                  <div key={a.name} className="flex items-center justify-between">
                    <span className="text-xs text-foreground">{a.name}</span>
                    <span className={`text-xs font-bold ${scoreColor(a.percentage)}`}>{a.percentage}%</span>
                  </div>
                ))}
              </div>
            </KCard>
          )}
        </div>
      )}

      {/* Score Trend */}
      {scoreTrend.length > 0 && (
        <KCard className="p-5">
          <p className="text-sm font-semibold text-foreground mb-4">Mock Test Score Trend</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={scoreTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" unit="%" />
              <Tooltip formatter={(v: number) => [`${v}%`, "Score"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </KCard>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Category bar chart */}
        {categoryData.length > 0 && (
          <KCard className="p-5">
            <p className="text-sm font-semibold text-foreground mb-4">Category Performance</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={70} />
                <Tooltip formatter={(v: number) => [`${v}%`, "Score"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="percentage" radius={[0, 4, 4, 0]}>
                  {categoryData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </KCard>
        )}

        {/* Pass/Fail donut */}
        {passFailData.length > 0 && (
          <KCard className="p-5">
            <p className="text-sm font-semibold text-foreground mb-4">Pass / Fail Distribution</p>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={passFailData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                  {passFailData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </KCard>
        )}
      </div>

      {/* Course progress bar chart */}
      {courseChart.length > 0 && (
        <KCard className="p-5">
          <p className="text-sm font-semibold text-foreground mb-4">Course Progress Overview</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={courseChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
              <Tooltip formatter={(v: number) => [`${v}%`, "Progress"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="progress" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </KCard>
      )}

      {!scoreTrend.length && !categoryData.length && !passFailData.length && (
        <EmptyState label="No analytics data available yet. The student needs to complete some exams or mock tests first." />
      )}
    </div>
  );
}

// ── Activity Timeline Tab ─────────────────────────────────────────────────────
function ActivityTimelineTab({ userId }: { userId: number }) {
  const { data: courses } = useQuery({
    queryKey: ["studentCourseProgress", userId],
    queryFn: () => getStudentCourseProgress(userId),
  });
  const { data: mockHistory } = useQuery({
    queryKey: ["studentMockTestHistory", userId],
    queryFn: () => getStudentMockTestHistory(userId),
  });

  const events = useMemo(() => {
    const list: { date: Date; icon: React.ReactNode; label: string; sub?: string }[] = [];

    // Course enrollments
    courses?.forEach(c => {
      list.push({
        date: new Date(c.enrolledAt),
        icon: <BookOpen size={12} />,
        label: `Enrolled in "${c.course.title}"`,
      });
      if (c.progress.lastAccessedAt) {
        list.push({
          date: new Date(c.progress.lastAccessedAt),
          icon: <Activity size={12} />,
          label: `Last accessed "${c.course.title}"`,
        });
      }
      c.chapters.forEach(ch => {
        ch.exam?.attempts.forEach(att => {
          if (att.submittedAt) {
            list.push({
              date: new Date(att.submittedAt),
              icon: att.isPassed ? <CheckCircle size={12} /> : <XCircle size={12} />,
              label: `${att.isPassed ? "Passed" : "Failed"} exam in "${ch.title}"`,
              sub: `${c.course.title} • Score: ${att.score}/${ch.exam!.totalMarks}`,
            });
          }
        });
      });
    });

    // Mock test purchases & attempts
    mockHistory?.forEach(item => {
      if (item.purchase && item.purchase.purchasedAt) {
        list.push({
          date: new Date(item.purchase.purchasedAt),
          icon: <FileText size={12} />,
          label: `Purchased "${item.mockTest.title}"`,
          sub: `${item.mockTest.examType} ${item.mockTest.level}`,
        });
      }
      item.attempts.forEach(att => {
        if (att.submittedAt) {
          list.push({
            date: new Date(att.submittedAt),
            icon: att.isPassed ? <Award size={12} /> : <AlertCircle size={12} />,
            label: `${att.isPassed ? "Passed" : "Failed"} Mock Test — "${item.mockTest.title}"`,
            sub: `Score: ${att.score}/${item.mockTest.totalMarks} • Attempt #${att.attemptNumber}`,
          });
        }
      });
    });

    return list.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [courses, mockHistory]);

  if (!events.length) return <EmptyState label="No activity recorded yet." />;

  return (
    <div className="relative">
      <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />
      <div className="space-y-4">
        {events.map((ev, i) => (
          <div key={i} className="flex gap-4 pl-5">
            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 -ml-4 border-2 border-background">
              {ev.icon}
            </div>
            <div className="flex-1 pb-1">
              <p className="text-sm font-medium text-foreground leading-tight">{ev.label}</p>
              {ev.sub && <p className="text-xs text-muted-foreground mt-0.5">{ev.sub}</p>}
              <p className="text-xs text-muted-foreground mt-0.5">{formatRelative(ev.date.toISOString())} — {formatDateTime(ev.date.toISOString())}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Admin Notes Tab ───────────────────────────────────────────────────────────
function AdminNotesTab({ userId }: { userId: number }) {
  const [draft, setDraft] = useState("");
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: notes, isLoading } = useQuery({
    queryKey: ["studentNotes", userId],
    queryFn: () => getStudentNotes(userId),
  });

  const createMut = useMutation({
    mutationFn: (content: string) => createStudentNote(userId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studentNotes", userId] });
      setDraft("");
    },
  });

  const deleteMut = useMutation({
    mutationFn: (noteId: string) => deleteStudentNote(userId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studentNotes", userId] });
    },
  });

  return (
    <div className="space-y-4">
      {/* Add note */}
      <KCard className="p-5">
        <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Plus size={14} className="text-primary" /> Add Internal Note
        </p>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Write an internal note about this student (visible only to admin and teachers)…"
          rows={3}
          className="w-full rounded-xl border border-border bg-muted/20 px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition-colors resize-none"
        />
        <div className="flex justify-end mt-3">
          <Button
            size="sm"
            disabled={!draft.trim() || createMut.isPending}
            onClick={() => createMut.mutate(draft.trim())}
          >
            {createMut.isPending ? "Saving…" : "Add Note"}
          </Button>
        </div>
      </KCard>

      {/* Notes list */}
      {isLoading ? (
        <LoadingState label="Loading notes…" />
      ) : !notes?.length ? (
        <div className="text-center py-10 text-sm text-muted-foreground">
          No notes yet. Add one above.
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map(note => (
            <KCard key={note.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground leading-relaxed">{note.content}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <span>{note.authorEmail}</span>
                    <span>•</span>
                    <span>{formatDateTime(note.createdAt)}</span>
                  </div>
                </div>
                {(user?.role === "admin" || user?.id === note.authorId) && (
                  <button
                    onClick={() => deleteMut.mutate(note.id)}
                    disabled={deleteMut.isPending}
                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1"
                    title="Delete note"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </KCard>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Utility Components ────────────────────────────────────────────────────────
function LoadingState({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}
function EmptyState({ label }: { label: string }) {
  return (
    <div className="dash-empty-state">{label}</div>
  );
}

// ── Export helper ──────────────────────────────────────────────────────────────
function exportCSV(profile: any, courses: any[], mockHistory: any[]) {
  const name = profile ? `${profile.profile?.firstName ?? ""} ${profile.profile?.lastName ?? ""}`.trim() || profile.user.email : "student";
  const rows: string[][] = [
    ["Student Analytics Export"],
    ["Name", name],
    ["Email", profile?.user.email ?? ""],
    [""],
    ["COURSE PROGRESS"],
    ["Course", "Progress %", "Enrolled Date", "Last Access", "Status"],
    ...(courses ?? []).map(c => [
      c.course.title,
      String(Math.round(c.progress.percentage)),
      formatDate(c.enrolledAt),
      formatDate(c.progress.lastAccessedAt),
      c.progress.isCompleted ? "Completed" : "In Progress",
    ]),
    [""],
    ["MOCK TEST HISTORY"],
    ["Test", "Level", "Exam Type", "Best Score", "Latest Score", "Attempts Used", "Purchased"],
    ...(mockHistory ?? []).map(m => [
      m.mockTest.title,
      m.mockTest.level,
      m.mockTest.examType,
      m.stats.bestScore !== null ? `${m.stats.bestScore}/${m.mockTest.totalMarks}` : "—",
      m.stats.latestScore !== null ? `${m.stats.latestScore}/${m.mockTest.totalMarks}` : "—",
      String(m.stats.attemptsUsed),
      m.purchase?.purchasedAt ? formatDate(m.purchase.purchasedAt) : "—",
    ]),
  ];

  const csv = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `student_analytics_${name.replace(/\s+/g, "_")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function StudentAnalyticsPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("profile");

  const numericUserId = Number(userId);

  const { data: profile, isLoading: profileLoading, isError: profileError } = useQuery({
    queryKey: ["studentProfile", numericUserId],
    queryFn: () => getStudentProfile(numericUserId),
    enabled: !!numericUserId,
  });
  const { data: courses } = useQuery({
    queryKey: ["studentCourseProgress", numericUserId],
    queryFn: () => getStudentCourseProgress(numericUserId),
    enabled: !!numericUserId,
  });
  const { data: mockHistory } = useQuery({
    queryKey: ["studentMockTestHistory", numericUserId],
    queryFn: () => getStudentMockTestHistory(numericUserId),
    enabled: !!numericUserId,
  });

  const studentName = profile ? displayName(profile.profile, profile.user.email) : "Student";
  const backPath = authUser?.role === "admin" ? "/admin/users" : "/teacher/students";

  if (profileLoading) {
    return (
      <DashboardLayout>
        <LoadingState label="Loading student analytics…" />
      </DashboardLayout>
    );
  }

  if (profileError || !profile) {
    return (
      <DashboardLayout>
        <div className="dash-page">
          <div className="text-center py-20">
            <AlertCircle size={32} className="text-destructive mx-auto mb-3" />
            <p className="text-sm text-destructive font-medium">Failed to load student profile.</p>
            <Button variant="secondary" size="sm" className="mt-4" onClick={() => navigate(backPath)}>
              Go Back
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dash-page print:p-0">
        {/* Header */}
        <div className="dash-page-header mb-6 print:hidden flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(backPath)}
              className="w-8 h-8 rounded-lg border border-border bg-card hover:bg-muted/40 flex items-center justify-center transition-colors"
            >
              <ArrowLeft size={14} />
            </button>
            <div>
              <h1 className="dash-page-title">Student Analytics</h1>
              <p className="dash-page-subtitle">{studentName}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => exportCSV(profile, courses ?? [], mockHistory ?? [])}
              className="gap-1.5"
            >
              <Download size={13} /> Export CSV
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.print()}
              className="gap-1.5"
            >
              <Download size={13} /> Print / PDF
            </Button>
          </div>
        </div>

        {/* Profile Summary Banner */}
        <KCard className="mb-6 overflow-hidden print:shadow-none">
          <div className="p-6 flex flex-col sm:flex-row gap-5">
            {/* Avatar */}
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
              {profile.profile?.image
                ? <img src={profile.profile.image} alt="" className="w-full h-full object-cover" />
                : <span className="text-2xl font-bold text-primary">{studentName.charAt(0).toUpperCase()}</span>
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-foreground">{studentName}</h2>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Mail size={11} /> {profile.user.email}
                    </span>
                    {profile.profile?.contactNumber && (
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Phone size={11} /> {profile.profile.contactNumber}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Calendar size={11} /> Joined {formatDate(profile.user.createdAt)}
                    </span>
                    {profile.stats.lastActivity && (
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock size={11} /> Active {formatRelative(profile.stats.lastActivity)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className={`dash-badge ${profile.user.isVerified ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                    {profile.user.isVerified ? "Verified" : "Unverified"}
                  </span>
                  <span className="dash-badge bg-secondary text-secondary-foreground">
                    {profile.user.role === "SYSTEM_ADMIN" ? "Admin" : profile.user.role === "TEACHER" ? "Teacher" : "Student"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="border-t border-border grid grid-cols-2 sm:grid-cols-5 divide-x divide-border">
            {[
              { label: "Courses Purchased", value: profile.stats.totalCoursesPurchased, color: "text-primary" },
              { label: "Courses Completed", value: profile.stats.totalCoursesCompleted, color: "text-emerald-600" },
              { label: "Mock Tests", value: profile.stats.totalMockTestsPurchased, color: "text-blue-600" },
              { label: "Total Attempts", value: profile.stats.totalMockTestsAttempted, color: "text-amber-600" },
              {
                label: "Avg Mock Score",
                value: profile.stats.averageMockTestScore !== null ? `${profile.stats.averageMockTestScore}%` : "—",
                color: profile.stats.averageMockTestScore !== null ? scoreColor(profile.stats.averageMockTestScore) : "text-muted-foreground",
              },
            ].map((s, i) => (
              <div key={i} className="text-center p-4">
                <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        </KCard>

        {/* Tab Nav */}
        <div className="flex border-b border-border mb-6 overflow-x-auto print:hidden">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === "profile" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <StatCard icon={<BookOpen size={18} />} label="Courses Enrolled" value={profile.stats.totalCoursesPurchased} color="primary" />
              <StatCard icon={<CheckCircle size={18} />} label="Courses Completed" value={profile.stats.totalCoursesCompleted} color="emerald" />
              <StatCard icon={<FileText size={18} />} label="Mock Tests Purchased" value={profile.stats.totalMockTestsPurchased} color="blue" />
              <StatCard icon={<TrendingUp size={18} />} label="Total Mock Attempts" value={profile.stats.totalMockTestsAttempted} color="amber" />
              <StatCard
                icon={<Target size={18} />}
                label="Average Mock Score"
                value={profile.stats.averageMockTestScore !== null ? `${profile.stats.averageMockTestScore}%` : "—"}
                color="purple"
              />
              <StatCard
                icon={<Clock size={18} />}
                label="Last Activity"
                value={profile.stats.lastActivity ? formatRelative(profile.stats.lastActivity) : "—"}
                sub={profile.stats.lastActivity ? formatDate(profile.stats.lastActivity) : undefined}
                color="primary"
              />
            </div>
          )}
          {activeTab === "courses" && <CourseProgressTab userId={numericUserId} />}
          {activeTab === "mock-tests" && <MockTestsTab userId={numericUserId} />}
          {activeTab === "analytics" && <AnalyticsTab userId={numericUserId} />}
          {activeTab === "timeline" && <ActivityTimelineTab userId={numericUserId} />}
          {activeTab === "notes" && <AdminNotesTab userId={numericUserId} />}
        </div>
      </div>
    </DashboardLayout>
  );
}
